// neo_cogym — 블록 에디터 호스트 (W6/W8)
// scratch-gui baseline 빌드 산출물을 iframe 으로 임베드. ★shim 주입 타이밍 정공법:
// 부모 window 에 __neoInstallShim 을 "모듈 로드 시점"에 등록 → iframe index.html 상단의
// 부트스트랩 <script> 가 번들 평가 '전에' window.parent.__neoInstallShim(window) 를 호출해
// window.electronAPI 를 보장한다(load 이후 주입의 경합 제거).
//
// baseline index.html 에 넣을 부트스트랩 한 줄:
//   <script>window.parent&&window.parent.__neoInstallShim&&window.parent.__neoInstallShim(window)</script>
// (public/scratch-gui/README.md 참조)

import { useEffect, useRef } from "react";
import { installElectronApiShim, type ShimHandle, type ShimOptions } from "../compat/electronApiShim";
import type { RelayConfig } from "../device/relayTransport";

const GUI_URL = import.meta.env.VITE_SCRATCH_GUI_URL ?? "/scratch-gui/index.html";

// 현재 EditorHost 인스턴스의 옵션/콜백을 부트스트랩에 전달하기 위한 모듈 레지스트리.
let activeConfig: {
  opts: ShimOptions;
  onShimReady?: (h: ShimHandle) => void;
} | null = null;

type ShimWindow = Window & {
  __neoInstallShim?: (frameWin: Window) => void;
};

// 모듈 로드 시점(=어떤 iframe 보다 먼저)에 전역 설치자를 등록한다.
if (typeof window !== "undefined") {
  (window as ShimWindow).__neoInstallShim = (frameWin: Window) => {
    if (!activeConfig) return;
    const handle = installElectronApiShim({ ...activeConfig.opts, targetWindow: frameWin });
    if (handle) activeConfig.onShimReady?.(handle);
  };
}

export interface EditorHostProps {
  relay?: RelayConfig;
  getAuthToken?: () => string | null;
  onDeviceStatus?: (s: string) => void;
  onShimReady?: (h: ShimHandle) => void;
}

export function EditorHost({ relay, getAuthToken, onDeviceStatus, onShimReady }: EditorHostProps) {
  const ref = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    activeConfig = { opts: { relay, getAuthToken, onDeviceStatus }, onShimReady };

    // 폴백: 부트스트랩이 없는(구) baseline 대비 — load 시점에도 1회 주입 시도.
    const iframe = ref.current;
    const onLoad = () => {
      const win = iframe?.contentWindow as ShimWindow | null | undefined;
      if (!win) return;
      if (!(win as unknown as { electronAPI?: unknown }).electronAPI) {
        (window as ShimWindow).__neoInstallShim?.(win);
      }
    };
    iframe?.addEventListener("load", onLoad);
    return () => {
      iframe?.removeEventListener("load", onLoad);
      activeConfig = null;
    };
  }, [relay, getAuthToken, onDeviceStatus, onShimReady]);

  return (
    <iframe
      ref={ref}
      src={GUI_URL}
      title="블록 에디터"
      style={{ width: "100%", height: "100%", border: "none", display: "block" }}
      allow="camera; microphone; serial; usb; bluetooth"
    />
  );
}
